/**
 * File Upload API Client
 * Handles multipart/form-data uploads to the backend file upload endpoint
 */

import type { IntegrationDirection } from '@steerfi/db';
import type { PaymentFileRowResult } from '@steerfi/shared';
import { config } from '@/config';
import { authenticatedApiCall, getAuthToken } from './keycloak-auth';

// ============================================================================
// Backend DTO Types (matching services/api-server/src/dtos/file.dto.ts)
// TODO: Move these to @steerfi/shared/types/dtos when refactoring
// ============================================================================

/**
 * File metadata response DTO from backend
 * Matches FileMetadataResponseDto in api-server
 */
export interface FileMetadataResponseDto {
  file_id: string;
  party_id: string | null;
  file_format_id: number;
  logical_name: string; // S3 key/filename (e.g., "abc-123-def.csv")
  description: string | null;
  file_direction: IntegrationDirection;
  origination_method_id: number;
  status_type_id: number;
  file_name: string; // Original uploaded filename
  file_url: string;
  file_size_bytes: string | null;
  content_type_id: number | null;
  s3_bucket?: string;
  s3_key?: string;
  s3_region?: string;
  created_at: Date;
  updated_at: Date;
}

/**
 * File deletion response DTO from backend
 * Matches FileDeleteResponseDto in api-server
 */
export interface FileDeleteResponseDto {
  file_id: string;
  deleted: boolean;
  deleted_from_s3: boolean;
  message: string;
  deleted_at?: Date;
}

/**
 * Signed URL response DTO from backend
 * Matches SignedUrlResponseDto in api-server
 */
export interface SignedUrlResponseDto {
  file_id: string;
  signed_url: string;
  expires_at: Date;
  expires_in_seconds: number;
}

// ============================================================================
// Frontend-Specific Types
// ============================================================================

/**
 * Frontend-specific request type for file uploads
 * Extends backend DTO with browser File object
 */
export interface FileUploadRequest {
  file: File; // Browser File object (not sent to backend as typed field)
  party_id: string;
  file_format_id?: number;
  file_type_id?: number;
  description?: string;
}

/**
 * Response type for file upload endpoint
 * NOTE: authenticatedApiCall unwraps { success, data } and returns just the data property
 */
export interface FileUploadResponse {
  file: FileMetadataResponseDto;
  s3_key: string;
  s3_url: string;
  uploaded_at: string;
  processed: boolean;
  processing_result?: {
    total_rows: number;
    processed_successfully: number;
    skipped_rows: number;
    failed_rows: number;
    processing_duration_ms: number;
  } | null;
}

/**
 * Response type for file metadata retrieval
 * NOTE: authenticatedApiCall unwraps { success, data } and returns just the data property
 */
export type FileMetadataResponse = FileMetadataResponseDto;

/**
 * Response type for file deletion
 * NOTE: authenticatedApiCall unwraps { success, data } and returns just the data property
 */
export type FileDeleteResponse = FileDeleteResponseDto;

/**
 * Response type for signed download URLs
 * NOTE: authenticatedApiCall unwraps { success, data } and returns just the data property
 */
export type DownloadUrlResponse = SignedUrlResponseDto;

/**
 * Response type for file processing endpoint
 * NOTE: authenticatedApiCall unwraps { success, data } and returns just the data property
 */
export interface FileProcessResponse {
  file_id: string;
  processed: boolean;
  processing_result: {
    total_rows: number;
    processed_successfully: number;
    skipped_rows: number;
    failed_rows: number;
    processing_duration_ms: number;
  };
}

// ============================================================================
// CSV Validation Types
// ============================================================================

/**
 * Column mapping result for CSV validation
 */
export interface CsvColumnMapping {
  source_column: string;
  source_position: number;
  target_table: string;
  target_field: string;
  is_required: boolean;
  is_matched: boolean;
}

/**
 * Validated row data with original and mapped values
 */
export interface CsvValidatedRow {
  row_number: number;
  is_valid: boolean;
  original_data: Record<string, string>;
  mapped_data: Record<string, string>;
  errors: string[];
  warnings: string[];
}

/**
 * Schema validation result
 */
export interface CsvSchemaValidationResult {
  is_valid: boolean;
  total_rows: number;
  valid_rows: number;
  invalid_rows: number;
  column_mappings: CsvColumnMapping[];
  validated_rows: CsvValidatedRow[];
  schema_errors: string[];
  field_errors: Array<{
    row_number: number;
    field: string;
    message: string;
    value?: string;
  }>;
}

/**
 * CSV validation request
 */
export interface CsvValidationRequest {
  file: File;
  party_id: string;
  file_format_id?: number;
  file_type_id?: number;
  description?: string;
}

/**
 * CSV validation success response
 * Note: File upload happens separately on Submit & Process, not during validation
 */
export interface CsvValidationSuccessResponse {
  success: true;
  message: string;
  data: {
    validation_result: CsvSchemaValidationResult;
    preview_rows: CsvValidatedRow[];
    converted_data: CsvValidatedRow[];
  };
}

/**
 * CSV validation error response
 */
export interface CsvValidationErrorResponse {
  success: false;
  error: string;
  code: string;
  validation_result?: CsvSchemaValidationResult;
  preview_rows?: CsvValidatedRow[]; // First 10 rows for UI display (shows which rows failed)
  details?: {
    total_rows?: number;
    schema_errors?: string[];
    field_errors?: Array<{
      row_number: number;
      field: string;
      message: string;
      value?: string;
    }>;
  };
}

/**
 * CSV validation response (union type)
 */
export type CsvValidationResponse = CsvValidationSuccessResponse | CsvValidationErrorResponse;

/**
 * Filters for listing files
 */
export interface FileListFilters {
  party_id?: string;
  file_format_id?: number;
  status_type_id?: number;
  /** ISO date string (YYYY-MM-DD) for inclusive lower bound on created_at. */
  from_date?: string;
  /** ISO date string (YYYY-MM-DD) for inclusive upper bound on created_at. */
  to_date?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Paginated file list response
 */
export interface PaginatedFileListResponse {
  items: FileMetadataResponseDto[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Payment file item with enriched payment count and total amount
 */
export interface PaymentFileItem extends FileMetadataResponseDto {
  payment_count: number;
  total_payment_amount: string | null;
  client_name: string | null;
  debit_account_identifier: string | null;
  collection_type: string | null;
  /** After processing: one entry per file row (success and failure). */
  row_results?: PaymentFileRowResult[] | null;
}

/**
 * Paginated payment files response (enriched for payment-specific display)
 */
export interface PaginatedPaymentFilesResponse {
  items: PaymentFileItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface PaymentUploadConfigResponse {
  allowedUploadTypes: Array<'ui_batch' | 'sftp'>;
  supportedUploadTypes: {
    uiBatch: boolean;
    sftp: boolean;
  };
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * List payment files specifically (uses specialized endpoint with enrichments)
 * This endpoint automatically filters for FILE_TYPE.PAYMENT and adds payment counts
 */
export async function getPaymentFiles(
  filters?: Omit<FileListFilters, 'file_format_id'>
): Promise<PaginatedPaymentFilesResponse> {
  const params = new URLSearchParams();

  // Don't include file_format_id - backend forces it to PAYMENT
  if (filters?.party_id) params.append('party_id', filters.party_id);
  if (filters?.status_type_id) params.append('status_type_id', filters.status_type_id.toString());
  if (filters?.from_date) params.append('from_date', filters.from_date);
  if (filters?.to_date) params.append('to_date', filters.to_date);
  if (filters?.page) params.append('page', filters.page.toString());
  if (filters?.limit) params.append('limit', filters.limit.toString());
  if (filters?.sortBy) params.append('sortBy', filters.sortBy);
  if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);

  const queryString = params.toString();
  return authenticatedApiCall<PaginatedPaymentFilesResponse>(
    `/payments/files${queryString ? `?${queryString}` : ''}`,
    {
      method: 'GET',
    }
  );
}

/**
 * Get a single payment file by ID, enriched with payment counts, totals, client info, and
 * row results (same shape as items returned by getPaymentFiles). Used by the detail subpage.
 */
export async function getPaymentFileById(fileId: string): Promise<PaymentFileItem> {
  return authenticatedApiCall<PaymentFileItem>(`/payments/files/${fileId}`, {
    method: 'GET',
  });
}

/**
 * Per-party cache of in-flight/resolved upload-config requests. Caching the
 * promise dedupes concurrent calls into a single network request and reuses the
 * result across tab navigation, instead of re-fetching whenever the payment
 * files page (or another consumer) mounts. Keyed by partyId; the "self" party
 * (no explicit partyId) uses a sentinel key.
 */
const SELF_UPLOAD_CONFIG_KEY = '__self__';
const uploadConfigCache = new Map<string, Promise<PaymentUploadConfigResponse>>();

/**
 * Drop the cached upload-config so the next call re-fetches. Pass a partyId to
 * invalidate a single party, or omit it to clear everything (e.g. after the
 * upload channels are changed on the client settings screen).
 */
export function invalidatePaymentUploadConfig(partyId?: string): void {
  if (partyId) {
    uploadConfigCache.delete(partyId);
  } else {
    uploadConfigCache.clear();
  }
}

/**
 * Get payment upload channel config for a party. Results are cached per party
 * (see {@link uploadConfigCache}); pass `{ force: true }` to bypass the cache.
 */
export async function getPaymentUploadConfig(
  partyId?: string,
  options?: { force?: boolean }
): Promise<PaymentUploadConfigResponse> {
  const cacheKey = partyId ?? SELF_UPLOAD_CONFIG_KEY;
  if (!options?.force) {
    const cached = uploadConfigCache.get(cacheKey);
    if (cached) return cached;
  }

  const params = new URLSearchParams();
  if (partyId) params.append('party_id', partyId);

  const queryString = params.toString();
  const request = authenticatedApiCall<PaymentUploadConfigResponse>(
    `/payments/upload-config${queryString ? `?${queryString}` : ''}`,
    {
      method: 'GET',
    }
  );
  uploadConfigCache.set(cacheKey, request);
  // Evict failed requests so a later call can retry instead of returning the rejection.
  request.catch(() => {
    if (uploadConfigCache.get(cacheKey) === request) {
      uploadConfigCache.delete(cacheKey);
    }
  });
  return request;
}

/**
 * List files with pagination and filters (generic endpoint for all file types)
 */
export async function getFiles(filters?: FileListFilters): Promise<PaginatedFileListResponse> {
  const params = new URLSearchParams();

  if (filters?.party_id) params.append('party_id', filters.party_id);
  if (filters?.file_format_id) params.append('file_format_id', filters.file_format_id.toString());
  if (filters?.status_type_id) params.append('status_type_id', filters.status_type_id.toString());
  if (filters?.page) params.append('page', filters.page.toString());
  if (filters?.limit) params.append('limit', filters.limit.toString());
  if (filters?.sortBy) params.append('sortBy', filters.sortBy);
  if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);

  const queryString = params.toString();
  return authenticatedApiCall<PaginatedFileListResponse>(
    `/files${queryString ? `?${queryString}` : ''}`,
    {
      method: 'GET',
    }
  );
}

/**
 * Upload a file with metadata (without processing)
 */
export async function uploadFile(request: FileUploadRequest): Promise<FileUploadResponse> {
  const formData = new FormData();
  formData.append('file', request.file);
  formData.append('party_id', request.party_id);

  if (request.file_format_id) {
    formData.append('file_format_id', request.file_format_id.toString());
  }

  if (request.file_type_id) {
    formData.append('file_type_id', request.file_type_id.toString());
  }

  if (request.description) {
    formData.append('description', request.description);
  }

  return authenticatedApiCall<FileUploadResponse>('/files/upload', {
    method: 'POST',
    body: formData,
    headers: {}, // Empty headers - let authenticatedApiCall handle Content-Type for FormData
  });
}

/**
 * Process an already-uploaded file
 */
export async function processFile(fileId: string): Promise<FileProcessResponse> {
  return authenticatedApiCall<FileProcessResponse>(`/files/${fileId}/process`, {
    method: 'POST',
  });
}

/**
 * Get file metadata by ID
 */
export async function getFileById(fileId: string): Promise<FileMetadataResponse> {
  return authenticatedApiCall<FileMetadataResponse>(`/files/${fileId}`, {
    method: 'GET',
  });
}

/**
 * Delete a file
 */
export async function deleteFile(
  fileId: string,
  options?: {
    hardDelete?: boolean;
    deleteFromS3?: boolean;
  }
): Promise<FileDeleteResponse> {
  const params = new URLSearchParams();
  if (options?.hardDelete) params.append('hardDelete', 'true');
  if (options?.deleteFromS3 !== undefined) {
    params.append('deleteFromS3', options.deleteFromS3 ? 'true' : 'false');
  }

  return authenticatedApiCall<FileDeleteResponse>(`/files/${fileId}?${params.toString()}`, {
    method: 'DELETE',
  });
}

/**
 * Get signed download URL for a file
 */
export async function getDownloadUrl(
  fileId: string,
  expiresIn?: number
): Promise<DownloadUrlResponse> {
  const params = new URLSearchParams();
  if (expiresIn) params.append('expiresIn', expiresIn.toString());

  return authenticatedApiCall<DownloadUrlResponse>(
    `/files/${fileId}/download?${params.toString()}`,
    {
      method: 'GET',
    }
  );
}

/**
 * Validate a CSV file against schema before processing
 * On success, the file is uploaded to S3 and validation results are returned
 *
 * NOTE: This function handles 400 responses specially - they contain validation
 * results that should be shown to the user, not treated as errors.
 */
export async function validateCsvFile(
  request: CsvValidationRequest
): Promise<CsvValidationResponse> {
  const formData = new FormData();
  formData.append('file', request.file);
  formData.append('party_id', request.party_id);

  if (request.file_format_id) {
    formData.append('file_format_id', request.file_format_id.toString());
  }

  if (request.file_type_id) {
    formData.append('file_type_id', request.file_type_id.toString());
  }

  if (request.description) {
    formData.append('description', request.description);
  }

  // Get auth token using shared helper
  const token = await getAuthToken();

  const headers: HeadersInit = {
    Accept: 'application/json',
  };
  if (token) {
    (headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }

  const url = `${config.api.baseURL}/files/validate`;

  const response = await fetch(url, {
    method: 'POST',
    body: formData,
    headers,
    credentials: 'include',
  });

  const body = await response.json();

  // Handle 400 responses with validation results (expected case for failed validation)
  if (response.status === 400 && body.validation_result) {
    return body as CsvValidationErrorResponse;
  }

  // Handle other errors
  if (!response.ok) {
    throw new Error(body.error || `HTTP ${response.status}`);
  }

  // Handle success - unwrap if needed
  if (body.success && body.data) {
    return {
      success: true,
      message: body.message || 'CSV validated and uploaded successfully',
      data: body.data,
    };
  }

  // Return as-is if already in expected format
  return body as CsvValidationResponse;
}
